import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@heroui/react";
import { toast } from "react-toastify";
import { getTemplate, login } from "../services/api";
import { getLogoBase64, useThemeStore } from "../utils/themeManager";
import useStore from "../store/useStore";
import { updateTheme, loadLogoBase64, resetTheme } from "../utils/themeManager";
import { authStorage } from "../services/auth-storage";

export function LoginPage() {
  const history = useHistory();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const setAuthUser = useStore(state => state.setAuthUser);
  const theme = useThemeStore(state => state.theme);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Por favor, preencha todos os campos", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      if (user && user.token) {
        localStorage.setItem('auth', JSON.stringify(user));
        setAuthUser(user as any);
        
        // Salva as credenciais criptografadas
        authStorage.saveCredentials(email, password);
        
        // Zera tudo antes de repreencher pra nao herdar tema/logo de outro cliente
        resetTheme();
        const newTheme = await getTemplate(Number(user?.bpo.id));
        if (newTheme) {
          updateTheme(newTheme);
          await loadLogoBase64();
        }
        (window as any).ReactNativeWebView?.postMessage(JSON.stringify({
          type: "SET_AUTH",
          payload: {
            theme: newTheme,
            user: user
          }
        }));
        history.push("/companies");
      } else {
        toast.error("Email ou senha incorretos", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          style: {
            top: '1rem', // Ajuste este valor conforme necessário
            margin: '0 auto',
          },
        });
      }
    } catch (error) {
      const response = (error as any)?.response;
      const errorMessage = response?.data?.messages?.error || "Ocorreu um erro ao fazer login";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    history.push("/forgot-password");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: theme.primaryColor }}
    >
      <div style={{ height: "30vh" }} className="flex items-center justify-center">
        <img
          src={getLogoBase64()}
          alt="PlayBPO"
          className="h-24"
        />
      </div>

      <div
        style={{
          height: "70vh",
          borderTopLeftRadius: 70,
          borderTopRightRadius: 70,
        }}
        className="bg-white w-full overflow-hidden"
      >
        <div className="max-w-md mx-auto pt-12 px-6 relative h-full flex flex-col">
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-2xl font-bold text-center mb-8">Entrar</h1>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  disabled={loading}
                />
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite sua senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <Button
                color="primary"
                onPress={handleLogin}
                className="w-full text-secondary rounded-xl py-6"
                isLoading={loading}
              >
                Login
              </Button>

              <div
                className="text-center text-sm text-gray-500 cursor-pointer hover:text-primary transition-colors"
                onClick={handleForgotPassword}
              >
                Esqueci a senha
              </div>
            </div>
          </div>
          <div className="mt-8 mb-8 flex justify-center items-center">
            <span className="text-gray-500">Desenvolvido por</span> <span className="font-semibold ml-1">PlayBPO</span>
          </div>
        </div>
      </div>
    </div>
  );
}